import {
    Button,
    Card,
    CardContent,
    Container,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import { useAuth } from '../../../context/AuthContext';
import { useFetch } from '../../../hooks/useFecth';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import { persistSessionUser } from '../../../services/authSession';
import { adminTeamLabel } from './adminTeams';

const schema = yup.object().shape({
    userName: yup.string().email('Informe um e-mail válido').required('Informe o e-mail'),
    password: yup
        .string()
        .transform(value => (value === '' ? undefined : value))
        .min(8, 'Mínimo de 8 caracteres'),
    confirmPassword: yup.string().when('password', {
        is: value => Boolean(value),
        then: s => s.required('Confirme a senha').oneOf([yup.ref('password')], 'As senhas devem ser iguais'),
        otherwise: s => s,
    }),
});

export default function AdminProfile() {
    const { user } = useAuth();
    const { data, mutate, isFetching } = useFetch('me');
    const { setErrors, enqueueSnackbar } = useErrors();
    const profile = data || user;

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: schema,
        initialValues: {
            userName: profile?.userName || '',
            password: '',
            confirmPassword: '',
        },
        onSubmit: async values => {
            try {
                const { data: body } = await server.put('me', {
                    userName: values.userName.trim(),
                    password: values.password || undefined,
                });
                persistSessionUser({
                    ...(user || {}),
                    ...(body?.data || {}),
                    userName: values.userName.trim(),
                });
                await mutate();
                formik.setFieldValue('password', '');
                formik.setFieldValue('confirmPassword', '');
                enqueueSnackbar('Perfil atualizado.', { variant: 'success' });
            } catch (error) {
                setErrors(error);
            }
        },
    });

    useEffect(() => {
        if (!profile) return;
        formik.setValues({
            userName: profile?.userName || '',
            password: '',
            confirmPassword: '',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.id, profile?.userName]);

    return (
        <Container maxWidth="sm">
            <Stack spacing={0.5} pb={2}>
                <Typography variant="h2">Perfil</Typography>
                <Typography color="text.secondary" variant="body2">
                    Seus dados de acesso ao admin Eurobem.
                </Typography>
            </Stack>

            <Card variant="outlined">
                <CardContent>
                    <Stack component="form" onSubmit={formik.handleSubmit} spacing={2} pt={1}>
                        <TextField
                            label="E-mail"
                            name="userName"
                            type="email"
                            value={formik.values.userName}
                            onChange={formik.handleChange}
                            error={formik.touched.userName && Boolean(formik.errors.userName)}
                            helperText={formik.touched.userName && formik.errors.userName}
                            fullWidth
                            disabled={isFetching}
                        />
                        <TextField
                            label="Cargo"
                            value={adminTeamLabel(profile?.team)}
                            fullWidth
                            disabled
                            helperText="O cargo é definido pela equipe na lista de usuários."
                        />
                        <TextField
                            label="Nova senha"
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={
                                (formik.touched.password && formik.errors.password) ||
                                'Deixe em branco para manter a senha atual.'
                            }
                            fullWidth
                        />
                        <TextField
                            label="Confirmar nova senha"
                            name="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)
                            }
                            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                            fullWidth
                        />
                        <Stack direction="row" justifyContent="flex-end">
                            <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
                                Salvar
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Container>
    );
}
